import rp, { AppComponent } from "@reporting/rp-app-host";
import { css, html, nothing, render } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { msg } from "@omegagrid/localize";
import { ChatInput, ChatInputEvent } from "./chatInput";
import { ChatMessage } from "@reporting/rp-types/ai";
import { ChatContent } from "./chatContent";
import { AIApplication } from "..";
import { ChatPoller, ChatUpdate, isChatPending, mergeMessages } from "../chatPoller";
import { ChatSettings } from "../types";

export class ChatEvent extends Event {

	readonly idChat?: number;
	readonly isNew: boolean;

	constructor(type: string, args?: Partial<ChatEvent>) {
		super(`chat.${type}`, { bubbles: true, composed: true });
		if (args) Object.assign(this, args);
	}

}

@customElement('rp-mf-ai-chat')
export class Chat extends AppComponent {

	static styles = [AppComponent.styles, css`
		:host {
			display: flex;
			flex-direction: column;
			height: 100%;
			padding: 10px;
			gap: 10px;
		}

		.chat-content {
			flex: 1;
			overflow: hidden;
		}

		.chat-input {
			flex: 0 0 100px;
			max-height: 200px;
		}

		rp-mf-ai-chatcontent, rp-mf-ai-chatinput {
			height: 100%;
		}

		.chat-unavailable {
			flex: 0 0 auto;
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 8px;
			padding: 14px;
			border: 1px solid var(--og-border-color);
			border-radius: var(--og-base-radius);
			background: var(--og-background-color);
			text-align: center;
			font-size: 13px;
			color: var(--og-text-color-3, #888);
		}

		.chat-unavailable og-icon {
			color: var(--og-accent-color);
			font-size: 15px;
		}
	`];

	@query('rp-mf-ai-chatcontent')
	chatContent: ChatContent;

	@query('rp-mf-ai-chatinput')
	chatInput: ChatInput;

	@property({type: Number})
	idChat: number;

	@property({type: Boolean})
	readonly: boolean;

	get service() { return rp.ms('ai') }

	// The async (background-worker) chat completion is polled by the app-level ChatPoller, so it
	// keeps running even when this component is detached (user switched tab/module). While connected,
	// we subscribe to its `messages` event to render live updates for our idChat.
	private _poller?: ChatPoller;

	connectedCallback() {
		super.connectedCallback();
		this._subscribe();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		// Leave the app-level poller running; just stop updating this (now detached) view.
		this._poller?.removeEventListener('messages', this._onPollerMessages as EventListener);
		this._updateWatch();
	}

	private async _subscribe() {
		const app = await rp.app('ai') as AIApplication;
		this._poller = app.poller;
		if (this.isConnected) this._poller.addEventListener('messages', this._onPollerMessages as EventListener);
		this._updateWatch();
	}

	// Tells the poller which chat is on screen, so a pending answer there is polled fast (live progress).
	private _watchedId?: number;
	private _updateWatch() {
		if (!this._poller) return;
		const id = this.isConnected && this.idChat > 0 ? this.idChat : undefined;
		if (id === this._watchedId) return;
		if (this._watchedId) this._poller.unwatch(this._watchedId);
		if (id) this._poller.watch(id);
		this._watchedId = id;
	}

	private _onPollerMessages = (e: CustomEvent<ChatUpdate>) => {
		if (e.detail.idChat !== this.idChat) return;
		this._applyMessages(e.detail.messages);
	};

	// Shows the server's message list (keeping the unchanged message objects, so finished messages are
	// not re-rendered on every poll) and puts the input into the matching state.
	private _applyMessages(messages: ChatMessage[]) {
		this.chatContent.messages = mergeMessages(this.chatContent.messages, messages);
		this._updateInputState(messages);
	}

	private _updateInputState(messages: ChatMessage[]) {
		if (!this.chatInput) return;
		const pending = isChatPending(messages);
		this.chatInput.disabledCommit = pending;
		// Stop needs the pending row's idz — the optimistic placeholder (not saved yet) can't be cancelled
		this.chatInput.cancellable = pending && !!messages[messages.length - 1]?.idz;
	}

	// Stop generating: the server marks the pending answer cancelled and the worker aborts at its next
	// progress tick. The response already carries the final state, so no need to wait for the poller.
	private _onChatInputCancel = async () => {
		const pending = this.chatContent.messages?.[this.chatContent.messages.length - 1];
		if (!pending?.idz || !isChatPending(this.chatContent.messages)) return;
		if (this.chatInput) this.chatInput.cancellable = false; // no double clicks
		try {
			this._applyMessages(await this.service.post<ChatMessage[]>(`cancel/${pending.idz}`, {}));
		} catch (err) {
			this._updateInputState(this.chatContent.messages);
			throw err;
		}
	};

	private _onChatInputCommit = async (e: ChatInputEvent) => {
		const isNew = !this.idChat;
		const message: ChatMessage = {
			content: e.text,
			model: e.model,
			role: 'user',
			...(e.attachments?.length ? { attachments: e.attachments } : {}),
		} as unknown as ChatMessage;

		// Render both messages optimistically with their author name already in place: the user's
		// name and the selected model. The server echoes back the same values, so the avatar column
		// keeps its width instead of resizing (jumping) a few seconds later when the answer arrives.
		const user = rp.userProvider?.user;
		this.chatContent.add({ ...message, login_name: user?.login_name, user_name: user?.user_name } as ChatMessage);
		this.chatContent.add({ role: 'assistant', model: e.model, loading: true } as ChatMessage);
		if (this.chatInput) this.chatInput.disabledCommit = true; // block until the answer arrives

		const body = {
			...message,
			include_data: e.includeData,
			include_definition: e.includeDefinition,
			include_group: e.includeGroup,
			enable_thinking: e.enableThinking,
		};
		let messages: ChatMessage[];
		try {
			// Returns immediately with a 'pending' assistant placeholder; the worker generates the answer in the background
			messages = await this.service.post<ChatMessage[]>(this.idChat ? `messages/${this.idChat}` : 'messages', body);
		} catch (err) {
			if (this.chatInput) this.chatInput.disabledCommit = false;
			throw err;
		}

		if (isNew) {
			this.idChat = messages[0].id_chat; // triggers updated() → loads messages and starts polling
		} else {
			this._applyMessages(messages);
			if (isChatPending(messages)) {
				const app = await rp.app('ai') as AIApplication;
				app.poller.track(this.idChat);
			}
		}
		rp.host.dispatchEvent(new ChatEvent('post', {isNew, idChat: this.idChat}));
	}

	// Remember the chat state (toggles + selected model) per user and accounting, so the next new chat
	// starts with it (served back by GET ai/default).
	private _onChatInputSettings = async (e: ChatInputEvent) => {
		const settings: ChatSettings = {
			includeData: e.includeData,
			includeDefinition: e.includeDefinition,
			includeGroup: e.includeGroup,
			enableThinking: e.enableThinking,
			...(e.model ? { model: e.model } : {}),
		};
		await rp.settings.set('ai.chat.settings', settings, 'user_accounting', rp.accountingProvider.a);
		(await rp.app('ai') as AIApplication).resetDefault();
	}

	private _defaultApplied = false;
	private _groupOfferApplied = false;
	private _flagsRestoredFor?: number;

	@state()
	private _available?: boolean;

	async firstUpdated() {
		if (this.readonly) return;
		const app = await rp.app('ai') as AIApplication;
		this._available = await app.isAvailable();
	}

	async updated() {
		const app = await rp.app('ai') as AIApplication;
		this._updateWatch();

		if (this.idChat > 0) {
			const messages = await this.service.get<ChatMessage[]>(`messages/${this.idChat}`);
			this._applyMessages(messages);
			// Resume polling if the conversation has an unfinished answer (e.g. after a page reload or returning from another module).
			if (isChatPending(messages)) app.poller.track(this.idChat);
		}

		// Whether the Group toggle is offered at all (an SA, or another company of the group to read) comes
		// with the default — for a new and for an existing chat alike.
		if (!this._groupOfferApplied && this.chatInput) {
			this._groupOfferApplied = true;
			const def = await app.getDefault();
			this.chatInput.canReadGroup = !!def?.canReadGroup;
		}

		// New chat: apply the configured default (model + context flags) once.
		if (!this._defaultApplied && !this.idChat && this.chatInput) {
			this._defaultApplied = true;
			const def = await app.getDefault();
			if (def) {
				if (def.model) this.chatInput.model = def.model;
				if (def.includeData !== undefined) this.chatInput.includeData = def.includeData;
				if (def.includeDefinition !== undefined) this.chatInput.includeDefinition = def.includeDefinition;
				if (def.includeGroup !== undefined) this.chatInput.includeGroup = def.includeGroup;
				if (def.enableThinking !== undefined) this.chatInput.enableThinking = def.enableThinking;
			}
		}

		if (this.chatInput && !this.chatInput.model) {
			let i = this.chatContent.messages?.length - 1;
			while (i >= 0 && !this.chatContent.messages[i].model) i--;
			if (i >= 0) this.chatInput.model = this.chatContent.messages[i].model;
		}

		if (this.chatInput && !this.chatInput.model) {
			const def = await app.getDefault();
			if (def?.model) this.chatInput.model = def.model;
		}

		if (this.idChat > 0 && this._flagsRestoredFor !== this.idChat && this.chatInput && this.chatContent.messages?.length) {
			const lastUserMsg = [...this.chatContent.messages].reverse().find(m => m.role === 'user');
			if (lastUserMsg) {
				this._flagsRestoredFor = this.idChat;
				this.chatInput.includeData = !!lastUserMsg.include_data;
				this.chatInput.includeDefinition = !!lastUserMsg.include_definition;
				this.chatInput.includeGroup = !!lastUserMsg.include_group;
				this.chatInput.enableThinking = !!lastUserMsg.enable_thinking;
			}
		}
	}

	private _renderFooter() {
		if (this.readonly) return nothing;
		if (this._available === true) {
			return html`
				<div class="chat-input">
					<rp-mf-ai-chatinput
						@chatinput.commit="${(e: ChatInputEvent) => this._onChatInputCommit(e)}"
						@chatinput.settings="${(e: ChatInputEvent) => this._onChatInputSettings(e)}"
						@chatinput.cancel="${() => this._onChatInputCancel()}">
					</rp-mf-ai-chatinput>
				</div>
			`;
		} else if (this._available === false) {
			return html`
				<div class="chat-unavailable">
					<og-icon icon="circle-info"></og-icon>
					<span>${msg('ai.notconfigured')}</span>
				</div>
			`;
		}
		return nothing;
	}

	render = () => html`
		<div class="chat-content">
			<rp-mf-ai-chatcontent></rp-mf-ai-chatcontent>
		</div>

		${this._renderFooter()}
	`;

}
